import { tv, type VariantProps } from "@ui/lib/tv";
import {
  type FlatIconName,
  flatIconMap,
  type IconName,
  type LineIconName,
  lineIconMap,
  type StickerIconName,
  type SvgComponent,
  stickerIconMap,
} from "./icons";

// One class per --sticker-* slot; values are colors.css scale tokens. The
// SVGs carry their canonical family as var() fallbacks, so these only take
// effect when the color prop is set.
const stickerColors = {
  green:
    "[--sticker-accent:var(--color-green-400)] [--sticker-bold:var(--color-green-500)] [--sticker-dark:var(--color-green-700)] [--sticker-pastel:var(--color-green-200)]",
  orange:
    "[--sticker-accent:var(--color-orange-400)] [--sticker-bold:var(--color-orange-500)] [--sticker-dark:var(--color-orange-600)] [--sticker-pastel:var(--color-orange-200)]",
  pink: "[--sticker-accent:var(--color-pink-200)] [--sticker-bold:var(--color-pink-300)] [--sticker-dark:var(--color-pink-600)] [--sticker-pastel:var(--color-pink-100)]",
  purple:
    "[--sticker-accent:var(--color-purple-500)] [--sticker-bold:var(--color-purple-500)] [--sticker-dark:var(--color-purple-600)] [--sticker-pastel:var(--color-purple-400)]",
  yellow:
    "[--sticker-accent:var(--color-yellow-500)] [--sticker-bold:var(--color-yellow-600)] [--sticker-dark:var(--color-yellow-700)] [--sticker-pastel:var(--color-yellow-300)]",
} satisfies Record<string, string>;

const icon = tv({
  compoundVariants: [
    // Shadow applies to stickers only; --sticker-shadow-scale (from the size
    // variant) keeps it proportional to the 32-unit sticker grid.
    { className: "drop-shadow-sticker-soft", shadow: "soft", type: "sticker" },
    { className: "drop-shadow-sticker-layered", shadow: "layered", type: "sticker" },
    // Tilt applies to stickers only. The angle rides a CSS variable so a call
    // site can deviate from the ±10° standard via className, e.g.
    // className="[--sticker-tilt:12deg]" (signed — the variable IS the angle).
    { className: "origin-center rotate-(--sticker-tilt) [--sticker-tilt:10deg]", tilt: "right", type: "sticker" },
    { className: "origin-center rotate-(--sticker-tilt) [--sticker-tilt:-10deg]", tilt: "left", type: "sticker" },
    // Color recolors sticker and flat icons; line icons follow currentColor.
    { className: stickerColors.green, color: "green", type: ["sticker", "flat"] },
    { className: stickerColors.orange, color: "orange", type: ["sticker", "flat"] },
    { className: stickerColors.pink, color: "pink", type: ["sticker", "flat"] },
    { className: stickerColors.purple, color: "purple", type: ["sticker", "flat"] },
    { className: stickerColors.yellow, color: "yellow", type: ["sticker", "flat"] },
  ],
  defaultVariants: {
    shadow: "soft",
    size: "20",
    type: "line",
  },
  variants: {
    color: {
      green: "",
      orange: "",
      pink: "",
      purple: "",
      yellow: "",
    },
    shadow: {
      soft: "",
      layered: "",
      none: "",
    },
    size: {
      "12": "size-3 [--sticker-shadow-scale:0.375]",
      "16": "size-4 [--sticker-shadow-scale:0.5]",
      "20": "size-5 [--sticker-shadow-scale:0.625]",
      "24": "size-6 [--sticker-shadow-scale:0.75]",
      "32": "size-8 [--sticker-shadow-scale:1]",
    },
    tilt: {
      right: "",
      left: "",
      none: "",
    },
    type: {
      line: "",
      // Sticker icons carry soft drop shadows that must bleed past a tight
      // viewBox instead of hard-clipping. Line and flat icons stay clipped.
      sticker: "overflow-visible",
      flat: "",
    },
  },
});

type IconVariants = VariantProps<typeof icon>;
type IconType = NonNullable<IconVariants["type"]>;
type IconShadow = NonNullable<IconVariants["shadow"]>;
export type IconColor = NonNullable<IconVariants["color"]>;
export type IconTilt = NonNullable<IconVariants["tilt"]>;

// Sticker geometry is authored upright; the set's signature +10° rightward
// lean comes from the tilt default instead. Exceptions mirror how each asset
// historically shipped: the date calendars leaned left, and a handful of
// glyphs stood upright.
const STICKER_TILT_DEFAULTS: Partial<Record<StickerIconName, IconTilt>> = {
  compass: "none",
  confetti: "none",
  dateApr: "left",
  dateAug: "left",
  dateDec: "left",
  dateFeb: "left",
  dateJan: "left",
  dateJul: "left",
  dateJun: "left",
  dateMar: "left",
  dateMay: "left",
  dateNov: "left",
  dateOct: "left",
  dateSep: "left",
  globeStand: "none",
  heartInactive: "none",
  menu: "none",
  search: "none",
  share: "left",
  tree: "none",
};

export interface IconProps {
  className?: string;
  /**
   * Palette family override (colors.css scale names). Ignored by line icons.
   * Unset, each sticker keeps its canonical family.
   */
  color?: IconColor;
  name: IconName;
  /** Sticker drop shadow. Ignored by line and flat icons. */
  shadow?: IconShadow;
  size?: IconVariants["size"];
  /**
   * Sticker lean (±10°). Ignored by line and flat icons. Defaults per icon to
   * the direction the asset historically leaned ("right" for most).
   */
  tilt?: IconTilt;
  type?: IconType;
}

export function Icon({ className, color, name, shadow, size, tilt, type = "line" }: IconProps) {
  const SvgComponent: SvgComponent | undefined =
    type === "sticker" && name in stickerIconMap
      ? stickerIconMap[name as StickerIconName]
      : type === "flat" && name in flatIconMap
        ? flatIconMap[name as FlatIconName]
        : name in lineIconMap
          ? lineIconMap[name as LineIconName]
          : undefined;

  if (!SvgComponent) return null;

  const effectiveTilt = tilt ?? STICKER_TILT_DEFAULTS[name as StickerIconName] ?? "right";

  return (
    <SvgComponent aria-hidden="true" className={icon({ color, shadow, size, tilt: effectiveTilt, type, className })} />
  );
}
